import { createContext, ReactNode, useContext, useId } from 'react';
import cx from 'classnames';

import { ChoiceSize, choiceBoxClasses, choiceLabelClasses } from '../checkbox';

interface RadioGroupContextValue {
  name: string;
  size: ChoiceSize;
  value: string;
  onChange: (value: string) => void;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioGroupProps<T extends string> {
  /** The question the options answer; becomes the group's `legend`. */
  label?: ReactNode;
  value: T;
  onChange: (value: T) => void;
  /** Size of every option: `sm` beside 14px text, `md` beside 16px. */
  size?: ChoiceSize;
  /** Layout of the options — the default is a column. */
  className?: string;
  children: ReactNode;
}

/** One choice out of several. The options are `Radio`s anywhere inside, so extra controls can sit beside them. */
export const RadioGroup = <T extends string>({
  label,
  value,
  onChange,
  size = 'md',
  className,
  children,
}: RadioGroupProps<T>) => {
  const name = useId();

  return (
    <fieldset className="min-w-0">
      {label && <legend className={cx('mb-3 text-ink', size === 'sm' ? 'text-ui' : 'text-body-l')}>{label}</legend>}
      <RadioGroupContext.Provider value={{ name, size, value, onChange: onChange as (value: string) => void }}>
        <div className={cx('flex flex-col gap-3', className)}>{children}</div>
      </RadioGroupContext.Provider>
    </fieldset>
  );
};

export interface RadioProps {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export const Radio = ({ value, label, disabled }: RadioProps) => {
  const group = useContext(RadioGroupContext);
  if (!group) throw new Error('Radio must be rendered inside a RadioGroup');

  return (
    <label className={choiceLabelClasses(group.size, disabled)}>
      <input
        type="radio"
        name={group.name}
        value={value}
        checked={group.value === value}
        disabled={disabled}
        onChange={() => group.onChange(value)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={cx(
          choiceBoxClasses(group.size, false, disabled),
          'rounded-full',
          disabled ? 'peer-checked:text-ink-subtle' : 'peer-checked:text-primary',
        )}
      >
        <span className={cx('rounded-full bg-current', group.size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
      </span>
      <span className="min-w-0">{label}</span>
    </label>
  );
};
